import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const FeatureAccessControl = ({ feature, children, fallbackContent }) => {
  const { user } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const checkFeatureAccess = async () => {
      if (!user) {
        setHasAccess(false);
        setLoading(false);
        setErrorMessage('Please log in to access this feature.');
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const response = await fetch(`${backendUrl}/api/subscription/current`, {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          setSubscription(data);
          
          const subscriptionName = data?.subscription?.subscription_name || 'Explorer';
          const packageConfig = data?.package_config || {};
          const features = packageConfig.features || {};
          
          // Check specific feature access
          const featureAccess = getFeatureAccess(feature, features, subscriptionName);
          setHasAccess(featureAccess.hasAccess);
          setErrorMessage(featureAccess.errorMessage);
        } else {
          setHasAccess(false);
          setErrorMessage('Unable to verify subscription status.');
        }
      } catch (error) {
        console.error('Error checking feature access:', error);
        setHasAccess(false);
        setErrorMessage('Error checking feature access.');
      } finally {
        setLoading(false);
      }
    };

    checkFeatureAccess();
  }, [user, feature]);

  const getFeatureAccess = (featureName, features, subscriptionName) => {
    switch (featureName) {
      case 'ai_immigration_assistant':
        if (!features.ai_immigration_assistant) {
          return {
            hasAccess: false,
            errorMessage: `AI Immigration Assistant is only available on paid plans. Your ${getSubscriptionDisplayName(subscriptionName)} plan doesn't include this feature. Please upgrade to Navigator ($39/month) or higher to access this feature.`
          };
        }
        break;
      
      case 'scan_uscis_notice':
        if (!features.scan_uscis_notice) {
          return {
            hasAccess: false,
            errorMessage: `Scan My USCIS Notice is only available on paid plans. Your ${getSubscriptionDisplayName(subscriptionName)} plan doesn't include this feature. Please upgrade to Navigator ($39/month) or higher to access this feature.`
          };
        }
        break;
      
      case 'review_case_modal':
        if (!features.review_case_modal) {
          return {
            hasAccess: false,
            errorMessage: `Review My Case feature is only available on paid plans. Your ${getSubscriptionDisplayName(subscriptionName)} plan only includes 'Ask a Question' access. Please upgrade to Navigator ($39/month) or higher to access case review features.`
          };
        }
        break;

      case 'multiple_agents':
        const maxAgents = features.ai_agents || 1;
        if (maxAgents < 2) {
          return {
            hasAccess: false,
            errorMessage: `Multiple AI agents are only available on Advocate ($79/month) or Champion ($149/month) plans. Your ${getSubscriptionDisplayName(subscriptionName)} plan allows access to ${maxAgents} agent. Please upgrade to access more agents.`
          };
        }
        break;

      default:
        break;
    }

    return { hasAccess: true, errorMessage: '' };
  };

  const getSubscriptionDisplayName = (subscriptionName) => {
    const displayNames = {
      'Explorer': 'free Explorer',
      'Navigator': 'Navigator ($39/month)',
      'Advocate': 'Advocate ($79/month)',
      'Champion': 'Champion ($149/month)'
    };
    return displayNames[subscriptionName] || subscriptionName;
  };

  if (loading) {
    return <div className="flex items-center justify-center p-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div></div>;
  }

  if (!hasAccess) {
    return fallbackContent || <FeatureLockScreen message={errorMessage} feature={feature} />;
  }

  return children;
};

const FeatureLockScreen = ({ message, feature }) => {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const getFeatureIcon = (featureName) => {
    switch (featureName) {
      case 'ai_immigration_assistant':
        return '🤖';
      case 'scan_uscis_notice':
        return '📄';
      case 'review_case_modal':
        return '🔍';
      case 'multiple_agents':
        return '👥';
      default:
        return '🔒';
    }
  };

  const getFeatureTitle = (featureName) => {
    switch (featureName) {
      case 'ai_immigration_assistant':
        return 'AI Immigration Assistant';
      case 'scan_uscis_notice':
        return 'Scan My USCIS Notice';
      case 'review_case_modal':
        return 'Review My Case';
      case 'multiple_agents':
        return 'Multiple AI Agents';
      default:
        return 'Premium Feature';
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg p-8 text-center border-2 border-gray-600">
      {/* Lock Icon */}
      <div className="text-6xl mb-4">
        🔒
      </div>
      
      {/* Feature Icon */}
      <div className="text-4xl mb-4">
        {getFeatureIcon(feature)}
      </div>

      {/* Title */}
      <h3 className="text-xl font-bold text-white mb-4">
        {getFeatureTitle(feature)} - Premium Feature
      </h3>

      {/* Message */}
      <p className="text-gray-300 mb-6 leading-relaxed">
        {message}
      </p>

      {/* Upgrade Button */}
      <button
        onClick={() => setShowUpgradeModal(true)}
        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center"
      >
        <span className="mr-2">🚀</span>
        Upgrade Plan
      </button>

      {/* Simple Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">Upgrade Your Plan</h3>
            <p className="text-gray-300 mb-6">
              Unlock this feature and many more by upgrading to a paid plan.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => window.location.href = '/pricing'}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                View Pricing Plans
              </button>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureAccessControl;